/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import * as yup from "yup";

/**
 * Funções puras extraídas do componente TCE (TCE/index.js).
 *
 * Estas funções foram MOVIDAS do componente para cá para:
 *   1. Permitir testes unitários isolados (sem React/axios/localStorage)
 *   2. Reduzir acoplamento no componente (que tem 4300+ linhas)
 *   3. Reutilização entre páginas se necessário
 *
 * Nenhuma função aqui tem side-effects, dependência de React,
 * localStorage, axios ou estado do componente.
 */

// ============================================
// CONSTANTES DE NEGÓCIO DO TCE
// ============================================

export const LINK_DOCUSIGN_FALLBACK =
  "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=72bb8440-acc9-4e15-b87e-f80823d9d92f&env=na2&acct=272fb0e3-8eca-4797-ac82-b4ba543c625f&v=2";

export const STATUS_LABELS = {
  AGUARDANDO_ESTUDANTE: "Aguardando preenchimento do Estudante",
  AGUARDANDO_VALIDACAO_CIEEMG: "Aguardando validação do CIEEMG",
  EM_VALIDACAO: "Em validação pelo CIEEMG",
  AGUARDANDO_CONFECCAO: "Aguardando confecção do contrato",
  CONTRATO_CONFECCIONADO: "Contrato confeccionado",
  AGUARDANDO_ASSINATURAS: "Aguardando assinaturas",
  DISPONIBILIZADO_PARA_ASSINATURA: "Disponibilizado para assinatura",
  FINALIZADO: "Finalizado",
  REPROVADO_EMPRESA: "Reprovado — voltar p/ Empresa",
  REPROVADO_ESTUDANTE: "Reprovado — voltar p/ Estudante",
  REPROVADO_DEFINITIVO: "Reprovado definitivamente",
  PENDENTE: "Pendente",
  APROVADO: "Aprovado",
  RECUSADO: "Recusado",
};

export const STATUS_CORES = {
  AGUARDANDO_ESTUDANTE: "#3b82f6",
  AGUARDANDO_VALIDACAO_CIEEMG: "#3b82f6",
  EM_VALIDACAO: "#1e40af",
  AGUARDANDO_CONFECCAO: "#3b82f6",
  CONTRATO_CONFECCIONADO: "#1e40af",
  AGUARDANDO_ASSINATURAS: "#3b82f6",
  DISPONIBILIZADO_PARA_ASSINATURA: "#1e40af",
  FINALIZADO: "#1e3a8a",
  REPROVADO_EMPRESA: "#ef4444",
  REPROVADO_ESTUDANTE: "#ef4444",
  REPROVADO_DEFINITIVO: "#dc2626",
  PENDENTE: "#3b82f6",
  APROVADO: "#1e3a8a",
  RECUSADO: "#dc2626",
};

// ============================================
// ESQUEMA DE VALIDAÇÃO (YUP)
// ============================================

export const schema = yup.object().shape({
  email: yup
    .string()
    .email("O campo e-mail é inválido")
    .required("O campo e-mail é obrigatório"),
});

// ============================================
// ANEXOS OBRIGATÓRIOS DO TCE
// ============================================

export const ANEXOS_SCHEMA_TCE = [
  {
    id: "declaracao_escolar",
    label: "Declaração escolar e/ou histórico de conclusão do Ensino Médio",
    icon: "GraduationCap",
    required: true,
  },
  {
    // `id` amigável (front-only). O back armazena na coluna
    // `anexo_declaracao_carga_horaria_*` (não em `anexo_carga_horaria_*`).
    // `idColumn` faz `buildAnexosObrigatoriosBodyPatch` e
    // `hydrateAnexosFilesFromPayload` espelharem todas as variações
    // de chave também com o nome real da coluna.
    id: "carga_horaria",
    idColumn: "declaracao_carga_horaria",
    label: "Declaração constando carga horária obrigatória",
    icon: "FileText",
    required: true,
  },
  {
    id: "documento_identificacao",
    label: "Documento de Identificação (RG ou CNH)",
    icon: "IdCard",
    required: true,
  },
];

// ============================================
// FUNÇÕES PURAS DE FORMATAÇÃO
// ============================================

/**
 * Formata string YYYY-MM-DD para DD/MM/YYYY (exibição).
 * Era usada inline no componente para mostrar datas.
 */
export const formatarDataBR = (dataStr) => {
  if (!dataStr) return "";
  const [yy, mm, dd] = dataStr.split("-");
  if (!yy || !mm || !dd) return dataStr;
  return `${dd}/${mm}/${yy}`;
};

/**
 * Formata telefone BR (10 ou 11 dígitos) para (DD) NNNNN-NNNN.
 * Para 10 dígitos sem 9 → (DD) NNNN-NNNN.
 * Para 11 dígitos com 9 → (DD) 9NNNN-NNNN.
 */
export const formatarTelefone = (tel) => {
  if (!tel) return "";
  const nums = tel.replace(/\D/g, "");
  if (nums.length === 11) {
    return nums.replace(/(\d{2})(\d{5})(\d{4})/, "($1)$2-$3");
  } else if (nums.length === 10) {
    return nums.replace(/(\d{2})(\d{4})(\d{4})/, "($1)$2-$3");
  }
  return tel;
};

/**
 * Formata CNPJ (14 dígitos) → XX.XXX.XXX/XXXX-XX ou
 * CPF (11 dígitos) → XXX.XXX.XXX-XX.
 * Qualquer outra quantidade → retorna o valor original sem máscara.
 */
export const formatarCnpj = (cnpj) => {
  if (!cnpj) return "";
  const nums = cnpj.replace(/\D/g, "");
  if (nums.length === 14) {
    return nums.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5",
    );
  } else if (nums.length === 11) {
    return nums.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  return cnpj;
};

/**
 * Remove tudo que não é dígito do CPF.
 */
export const normalizeCPF = (cpf) => (cpf ? cpf.replace(/\D/g, "") : "");

// ============================================
// FUNÇÕES PURO DE CÁLCULO/REGRAS
// ============================================

/**
 * Calcula a data máxima permitida para o Término do estágio
 * (até 2 anos após o Início). Retorna string no formato YYYY-MM-DD
 * ou "" se o Início não estiver preenchido.
 *
 * @param {string} inicioEstagio - data no formato YYYY-MM-DD
 */
export const calcularMaxTerminoEstagio = (inicioEstagio) => {
  if (!inicioEstagio) return "";
  const [yy, mm, dd] = inicioEstagio.split("-").map(Number);
  if (!yy || !mm || !dd) return "";
  const limite = new Date(yy, mm - 1, dd);
  limite.setFullYear(limite.getFullYear() + 2);
  return [
    limite.getFullYear(),
    String(limite.getMonth() + 1).padStart(2, "0"),
    String(limite.getDate()).padStart(2, "0"),
  ].join("-");
};

/**
 * Verifica se hora e minuto formam um horário válido (00:00 a 23:59).
 *
 * @param {string|number} hora - hora (0-23)
 * @param {string|number} minuto - minuto (0-59)
 * @returns {boolean} true se for um horário válido
 */
export const horarioValido = (hora, minuto) => {
  const h = Number(hora);
  const m = Number(minuto);

  return !isNaN(h) && !isNaN(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59;
};

/**
 * Calcula horas decimais entre dois horários (considerando intervalo).
 * Suporta virada de meia-noite (24h wrap).
 *
 * @param {string} entrada - horário de entrada (HH:MM)
 * @param {string} saida - horário de saída (HH:MM)
 * @param {string} [intervaloInicio] - horário de início do intervalo
 * @param {string} [intervaloFim] - horário de fim do intervalo
 * @returns {number} total de horas decimais
 */
export function horasDecimais(
  entrada,
  saida,
  intervaloInicio = "",
  intervaloFim = "",
) {
  if (!entrada || !saida) return 0;

  const [eh, em] = entrada.split(":").map(Number);
  const [sh, sm] = saida.split(":").map(Number);
  let totalMin = sh * 60 + sm - (eh * 60 + em);

  if (totalMin < 0) totalMin += 24 * 60;

  if (intervaloInicio && intervaloFim) {
    const [ih, im] = intervaloInicio.split(":").map(Number);
    const [fh, fm] = intervaloFim.split(":").map(Number);
    let intervaloMin = fh * 60 + fm - (ih * 60 + im);
    if (intervaloMin < 0) intervaloMin += 24 * 60;
    totalMin -= intervaloMin;
  }

  return totalMin / 60;
}

/**
 * Valida uma estrutura de horários de estágio.
 *
 * Regras:
 *   - Carga horária diária não pode ultrapassar 6h
 *   - Jornada de 6h exige intervalo (qualquer um)
 *   - Carga horária semanal não pode ultrapassar 30h
 *
 * @param {object} horario - estrutura com { tipo, diasSelecionados, fixo, alternado }
 * @returns {{ errors: string[], totalSemanal: number }}
 */
export function validarHorarios(horario) {
  const errors = [];
  let totalSemanal = 0;

  if (horario.tipo === "fixo") {
    const f = horario.fixo;

    if (f.entrada && f.saida) {
      const horas = horasDecimais(
        f.entrada,
        f.saida,
        f.intervaloInicio,
        f.intervaloFim,
      );

      if (horas > 6)
        errors.push("Carga horária diária não pode ultrapassar 6h");
      if (horas >= 6 && (!f.intervaloInicio || !f.intervaloFim)) {
        errors.push("Intervalo obrigatório para jornada de 6h");
      }

      totalSemanal = horas * horario.diasSelecionados.length;
    }
  } else if (horario.tipo === "alternado") {
    const diasMap = {
      segunda: "Segunda",
      terca: "Terça",
      quarta: "Quarta",
      quinta: "Quinta",
      sexta: "Sexta",
      sabado: "Sábado",
      domingo: "Domingo",
    };

    Object.keys(horario.alternado).forEach((key) => {
      const diaLabel = diasMap[key];
      const h = horario.alternado[key];

      if (h.entrada && h.saida) {
        const horas = horasDecimais(
          h.entrada,
          h.saida,
          h.intervaloInicio,
          h.intervaloFim,
        );
        if (horas > 6) errors.push(`${diaLabel}: carga horária superior a 6h`);
        if (horas >= 6 && (!h.intervaloInicio || !h.intervaloFim)) {
          errors.push(`${diaLabel}: intervalo obrigatório para 6h`);
        }
        totalSemanal += horas;
      }
    });
  }

  if (totalSemanal > 30) {
    errors.push(
      `Carga horária semanal (${totalSemanal.toFixed(1)}h) superior ao limite de 30h`,
    );
  }
  return { errors, totalSemanal };
}

// ============================================
// DIAS DA SEMANA (LABELS PARA UI)
// ============================================

export const DIAS_SEMANA = [
  { label: "Segunda", key: "segunda" },
  { label: "Terça", key: "terca" },
  { label: "Quarta", key: "quarta" },
  { label: "Quinta", key: "quinta" },
  { label: "Sexta", key: "sexta" },
  { label: "Sábado", key: "sabado" },
  { label: "Domingo", key: "domingo" },
];

/**
 * Extrai um array de CPFs normalizados (apenas dígitos) a partir
 * de um objeto de dados. Usado por hasDuplicateCPF.
 *
 * @param {object} data - objeto com campos de CPF
 * @param {string[]} fields - lista de chaves a extrair
 * @returns {string[]} array de CPFs normalizados (sem duplicatas vazias)
 */
export const extractCpfs = (data, fields) => {
  const values = fields.map((f) => normalizeCPF(data[f]));
  return values.filter((v) => v !== "");
};

/**
 * Verifica se há CPF duplicado entre os campos do objeto.
 * Refactor da função inline no componente para receber parâmetros
 * em vez de fechar sobre formData.
 *
 * @param {object} data - objeto com campos de CPF
 * @param {string[]} [fields] - opcional. Defaults aos campos conhecidos.
 * @returns {boolean} true se houver duplicata
 */
export const hasDuplicateCPF = (data, fields) => {
  // Regras:
  //  - cnpj_cpf e cpfCnpj (CNPJs matriz/filial) NÃO são comparados:
  //    empresa individual (MEI/EI) tem o mesmo CNPJ/CPF raiz e PJ
  //    tem filiais com CNPJ distinto (0001/0002/0003) — coincidência
  //    não é erro.
  //  - cpf_estagiario (supervisor) NÃO é comparado: pode ser o
  //    próprio representante legal da empresa.
  //  - cpf_estudante_estagiario x cpf_menor_idade SÓ são comparados
  //    quando o estudante for de fato MENOR de idade. Quando maior,
  //    cpf_menor_idade deve estar vazio — se o backend devolveu o
  //    CPF do estudante nesse campo por padrão, ignoramos.
  const cpfFields = fields || [
    "cpf_estudante_estagiario",
    "cpf_menor_idade",
  ];
  const dataCopy = { ...(data || {}) };

  // Se o estudante é maior de idade, NÃO inclui cpf_menor_idade na
  // checagem — esse campo não se aplica e o backend pode ter vindo
  // preenchido com o CPF do próprio estudante por default.
  const isMenor = String(
    dataCopy.maior_menor_idade || "",
  ).toUpperCase() === "MENOR";
  if (!isMenor) {
    dataCopy.cpf_menor_idade = "";
  }

  const filtered = extractCpfs(dataCopy, cpfFields);
  return new Set(filtered).size !== filtered.length;
};

/**
 * Verifica se o valor (de NumericFormat) é um número positivo.
 * Usado como validador customizado do NumericFormat.
 *
 * @param {object} values - objeto do NumericFormat { floatValue, ... }
 * @returns {boolean} true se floatValue > 0
 */
export const isPositiveNumber = (values) => {
  return values.floatValue > 0;
};
